import { useMemo } from "react";
import { query, where, documentId } from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { Ref } from "refs";
import { useSetfanSweeps } from "./useSetfanSweeps";
import { WinnerConversation } from "../WinnerRow";

/**
 *
 * previously, we created conversations for winners by adding their uid into
 * the conversations `user.uid` field and the current `sweepsId` in the `metadata.sweepsId` field.
 * we now generate the conversationId programatically instead of randomly, so we can determine
 * a winner's conversation with just a surveyId and uid.
 *
 * we still have legacy conversations in prod, so this hook will return conversations
 * for a winner with the new id format, if available. it will fallback to a legacy query
 * if no conversation can be found by building the id.
 */
export const useWinnersConversations = ({ surveyId }: { surveyId: string }) => {
  const { activeSweeps } = useSetfanSweeps({ surveyId });

  const conversationIds = useMemo(
    () => activeSweeps?.winners?.map((winner) => `${surveyId}#${winner}`) ?? [],
    [activeSweeps?.winners, surveyId],
  );

  const conversationsQueryV2 = conversationIds.length
    ? query(Ref("conversations"), where(documentId(), "in", conversationIds))
    : undefined;
  const [conversationsV2, conversationsV2Loading] =
    useCollection(conversationsQueryV2);

  const legacyConversationsQuery = activeSweeps
    ? query(
        Ref("conversations"),
        where("metadata.sweepsId", "==", activeSweeps.id),
      )
    : undefined;
  const [legacyConversations, legacyConversationsLoading] = useCollection(
    legacyConversationsQuery,
  );

  const conversations = useMemo(() => {
    if (
      conversationsV2Loading ||
      legacyConversationsLoading ||
      conversationIds.length === 0 ||
      !activeSweeps
    ) {
      return {};
    }
    // all winners have new conversations documents
    if (conversationsV2?.docs.length === conversationIds.length) {
      return conversationsV2.docs.reduce((acc, doc) => {
        acc[doc.id.split("#")[1]] = { id: doc.id, ...doc.data() };
        return acc;
      }, {} as Record<string, WinnerConversation>);
    }

    return activeSweeps.winners.reduce((acc, winnerHash) => {
      const v2Conversation = conversationsV2?.docs.find(
        (conv) => conv.id === `${surveyId}#${winnerHash}`,
      );

      if (v2Conversation) {
        acc[winnerHash] = { id: v2Conversation.id, ...v2Conversation.data() };
      } else {
        // legacy conversations can be retrieved by querying for the corresponding `sweepsId`
        // in the `conversations` collection
        let legacyConversation:
          | (WinnerConversation & { winnerHash: string })
          | undefined = undefined;

        for (const legacyConvo of legacyConversations?.docs ?? []) {
          const uid = legacyConvo.data().user?.uid;
          if (!uid) {
            continue;
          }
          // There was a short time we set `uid` as `fan#{fan email hash}`
          // so need to handle these.
          const uidHash = uid.replaceAll("fan#", "");
          if (uidHash === winnerHash) {
            legacyConversation = {
              id: legacyConvo.id,
              winnerHash: uidHash,
              ...legacyConvo.data(),
            };
          }

          if (legacyConversation) {
            const { winnerHash, ...conversation } = legacyConversation;
            acc[winnerHash] = conversation;
          }
        }
      }

      return acc;
    }, {} as Record<string, WinnerConversation>);
  }, [
    conversationsV2,
    conversationIds,
    activeSweeps,
    conversationsV2Loading,
    legacyConversationsLoading,
    surveyId,
    legacyConversations,
  ]);

  return {
    loading: conversationsV2Loading || legacyConversationsLoading,
    conversations,
  };
};
